import imageCompression from 'browser-image-compression';

const MAX_WIDTH_OR_HEIGHT = 1920;
const MAX_SIZE_MB = 0.5;
const HEIC_QUALITY = 0.92;
const JPEG_QUALITY = 0.92;

export class UnsupportedImageError extends Error {
  constructor(message) {
    super(message);
    this.name = 'UnsupportedImageError';
  }
}

const SUPPORTED_EXTENSIONS = [
  'jpg',
  'jpeg',
  'jpe',
  'jfif',
  'jps',
  'png',
  'gif',
  'webp',
  'bmp',
  'ico',
  'heic',
  'heif',
  'avif',
];

const SUPPORTED_MIME =
  /^image\/(jpeg|png|gif|webp|bmp|x-ms-bmp|vnd\.microsoft\.icon|x-icon|hei[cf]|avif)/i;

export const SUPPORTED_IMAGE_LABEL = 'JPG, PNG, GIF, WEBP, BMP, ICO, HEIC or AVIF';

export const SUPPORTED_IMAGE_ACCEPT = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/x-icon',
  'image/heic',
  'image/heif',
  'image/avif',
  ...SUPPORTED_EXTENSIONS.map((extension) => `.${extension}`),
].join(',');

export const isSupportedImage = (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return SUPPORTED_EXTENSIONS.includes(extension) || SUPPORTED_MIME.test(file.type);
};

const HEIC_MIME = /^image\/hei[cf]/i;
const HEIC_EXTENSION = /\.hei[cf]$/i;

const looksLikeHeic = (file) => HEIC_MIME.test(file.type) || HEIC_EXTENSION.test(file.name);

const AVIF_MIME = /^image\/avif/i;
const AVIF_EXTENSION = /\.avif$/i;

const looksLikeAvif = (file) => AVIF_MIME.test(file.type) || AVIF_EXTENSION.test(file.name);

export const needsConversion = (file) => looksLikeHeic(file) || looksLikeAvif(file);

async function avifToJpeg(file) {
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('No canvas context');

    // JPEG has no transparency; without this, transparent pixels turn black.
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0);
    bitmap.close();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) throw new Error('Canvas produced no image');

    return new File([blob], `${file.name.replace(AVIF_EXTENSION, '')}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    throw new UnsupportedImageError('This AVIF photo could not be read');
  }
}

const loadHeicConverter = async () => {
  const mod = await import('heic2any');
  return mod?.default ?? mod;
};

/**
 * Fetch the converter chunk ahead of time, so an iPhone photo taken later with
 * no signal can still be converted. Safe to call repeatedly.
 */
export const warmHeicConverter = () => {
  loadHeicConverter().catch(() => {});
};

async function heicToJpeg(file) {
  let heic2any;

  try {
    heic2any = await loadHeicConverter();
  } catch {
    return file;
  }

  try {
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: HEIC_QUALITY,
    });

    const blob = Array.isArray(converted) ? converted[0] : converted;

    return new File([blob], `${file.name.replace(HEIC_EXTENSION, '')}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    throw new UnsupportedImageError('This HEIC photo could not be read');
  }
}

export async function compressImage(file, options = {}) {
  const source = looksLikeHeic(file)
    ? await heicToJpeg(file)
    : looksLikeAvif(file)
      ? await avifToJpeg(file)
      : file;

  if (!source.type.startsWith('image/')) return source;

  try {
    const compressed = await imageCompression(source, {
      maxWidthOrHeight: options.maxWidthOrHeight ?? MAX_WIDTH_OR_HEIGHT,
      maxSizeMB: options.maxSizeMB ?? MAX_SIZE_MB,
      onProgress: options.onProgress,
      useWebWorker: false,
    });

    return compressed.size < source.size ? compressed : source;
  } catch {
    return source;
  }
}
